import React, {useContext, useEffect, useState} from 'react';
import styled from "styled-components";
import {
    voting_eventDateTimeResolver,
    voting_getIsWinner,
    voting_handleSelection,
    voting_rankedChoiceResolver,
    voting_shouldDisableButton
} from "./utilities";
import {api_closePoll, api_deletePoll, api_votePoll} from "./api";
import {useCommunity} from "../../../config/community";
import dayjs from "dayjs";
import Button from "../button";
import {CheckIcon} from "@heroicons/react/20/solid";
import SimpleTooltip from "../tooltip";
import {InlineLoader} from "../admin-activity";
import Field, {FieldInput, FieldLabel} from "../form/field";
import EntityTabs from "../entity-tabs";
import {DotsOptionsPopup} from "../../../routes/community/message";
import {EventDatetimeView} from "./event-datetime-view";

function getWeekFromNowDate() {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
}

const poll = {
    status: 'open',
    vote_visibility: 'counts', // counts = only how many voted in total and for each, general = who voted, detailed = who voted for what
    opens: null, // straight away
    closes: getWeekFromNowDate(),

    can_change_vote: false,

    show_results: 'immediately', // poll-close

    voter_count: 0,
    results: {},

    voting_method: 'ranked-choice-all', // first-past-the-post, ranked-choice-all, ranked-choice-any, majority, rating-all, rating-any, correct-answer
    title: '',
    choices_data: {
        /*
        '1234': {
            text: 'Thursday'
        },
        '7345': {
            text: 'Saturday'
        },
        '5236': {
            text: 'Monday'
        },
        '5235': {
            text: 'Tuesday'
        }

         */
    },
    choices: [
        //  '1234', '7345', '5236', '5235'
    ],
    who_can_vote: {
        all_members: true,
        entities: {},
        user_uids: {}
    },
    vote_history: {},
    votes: {
        /*
        '1234': {
            'XTMF': true
        }

         */
    },
    voted: {
        // 'XTMF': true
    }
};


function Title({title}) {
    return <div className="text-xl font-semibold text-gray-800">{title}{!title &&
        <span className="text-gray-400 italic">Untitled Poll</span>}</div>
}

function renderTimeLeft(minutes) {
    const hours = minutes / 60;
    const days = hours / 24;

    if (minutes < 2) {
        return 'Closes shortly'
    } else if (minutes < 58) {
        return `Closes in ${minutes} minutes`
    } else if (days > 2) {
        return `Closes in ${hours} hours`
    } else {
        return `Closes in ${days} days`
    }
}

function TimeLeft({closes, opens, status, show_results}) {

    if (status === 'closed') {
        return <div>
            Poll closed
        </div>
    }

    //  const minutes = dayjs.duration(dayjs(closes).diff(dayjs())).minutes();

    // {renderTimeLeft(minutes)}
    return <div>
        Closes {dayjs(closes).fromNow()}
    </div>
}

function Actions({vote, voted, status, poll, show_results, totals, disable_button}) {
    const primary_button_text = poll.voting_method === "event-datetime" ? "Confirm" : "Vote";
    const responses_type_text = poll.voting_method === "event-datetime" ? "response" : "vote";
    const {opens, closes} = poll;
    return <div className="flex space-x-4 items-center">
        {!voted && poll.status === 'open' && <div>
            <Button intent="primary" text={primary_button_text} onClick={() => vote()} disabled={disable_button}/>
        </div>}
        <div className="flex space-x-2 text-sm text-gray-500">
            <div>{totals.total} {responses_type_text}{totals.total === 1 ? "" : "s"}</div>
            <div>·</div>
            <TimeLeft status={status} opens={opens} closes={closes}/>
        </div>
    </div>
}

const Square = styled.div`

`;

function ChoiceIcon({voting_method, is_winner, show_results, number}) {

    if (voting_method === 'ranked-choice-all' || voting_method === 'ranked-choice-any') {
        return <div
            className={`h-svg-4 text-xs font-medium ${show_results && is_winner ? "text-white" : show_results ? "text-gray-600" : "text-white"}`}>
            {number}
        </div>
    }

    return <div
        className={`h-4 svg-child-inherit ${show_results && is_winner ? "text-white" : show_results ? "text-gray-600" : "text-white"}`}>
        <CheckIcon/>
    </div>
}

function getPercent(integer) {
    return `${(integer * 100).toFixed(1)}%`
}

function getStyles(selected, show_results, is_winner) {
    let label = "";
    let container = "";

    if (show_results) {
        if (selected) {
            container = "";
        } else {
            container = "bg-white";
        }
    } else {
        if (selected) {
            container = "";
        } else {
            container = "bg-white hover:bg-gray-100";
        }
    }

    if (is_winner) {
        label = "text-white";
    } else {
        label = "text-gray-700";
    }

    return {
        label,
        container
    }
}

function getWidth(votes_count, totals, id) {
    const actual = (votes_count / totals.total) * 100 + 6;

    return actual > 100 ? "100%" : (actual < 7) ? "10%" : `${actual}%`;
}

function getRankedChoiceWidth(votes_count, totals, id) {
    const actual = (votes_count / totals.total) * 100 + 6;

    return actual > 100 ? "100%" : (actual < 7) ? "10%" : `${actual}%`;
}

function showAsEliminated(totals, id, selection, poll) {
    if (poll.voting_method === 'ranked-choice-all') {
        if (selection === 'Results') {
            const penultimate_item = totals.rounds_display[totals.rounds_display.length - 2];
            return !!penultimate_item[1][id];
        } else {
            const item = totals.rounds_display.filter(a => a[0] === selection)[0];
            return !!item[1][id];
        }
    }
    return false;
}

function getVoteCount(totals, id, selection, poll) {
    if (poll.voting_method === 'ranked-choice-all') {
        if (selection === 'Results') {
            const penultimate_item = totals.rounds_display[totals.rounds_display.length - 2];
            return penultimate_item[1][id] || 0
        } else {
            const item = totals.rounds_display.filter(a => a[0] === selection)[0];
            return item[1] && item[1][id] ? item[1][id] : 0
        }
    }
    return totals[id] || 0;
}

function SelectedBg({totals, eliminated, votes_count, voting_method, selection, id, is_winner}) {

    if (voting_method === 'ranked-choice-all') {
        const width = getRankedChoiceWidth(votes_count, totals, id, selection);
        return <div style={{width}}
                    className={`absolute top-0 left-0 bottom-0 rounded-md ${is_winner ? "bg-secondary" : eliminated ? "bg-gray-200" : "bg-primary-50"} `}>

        </div>
    }

    const width = getWidth(votes_count, totals, id);

    return <div style={{width}}
                className={`absolute top-0 left-0 bottom-0 rounded-md ${is_winner ? "bg-secondary" : eliminated ? "bg-gray-200" : "bg-primary-50"} `}>

    </div>
}

function getVotesText(totals, id, selection, poll) {
    if (poll.voting_method === 'ranked-choice-all' || poll.voting_method === 'ranked-choice-any') {
        if (selection === 'Results') {
            const penultimate_item = totals.rounds_display[totals.rounds_display.length - 2];
            // account for 1 or many votes
            return `${penultimate_item[1][id]} vote${penultimate_item[1][id] === 1 ? "" : "s"}`;
        } else {
            const item = totals.rounds_display.filter(a => a[0] === selection)[0];
            // account for 1 or many votes
            return `${item[1][id]} vote${item[1][id] === 1 ? "" : "s"}`;
        }
    }
    return `${totals[id]} votes`;
}

function Choice({
                    text,
                    onSelect,
                    totals,
                    voted,
                    selection,
                    show_your_results,
                    id,
                    is_winner,
                    show_results,
                    poll,
                    number,
                    can_select,
                    selected
                }) {

    const votes_count = show_results ? getVoteCount(totals, id, selection, poll, show_results) : 0;
    const show_as_eliminated = !show_results ? false : showAsEliminated(totals, id, selection, poll);
    const {container, label} = getStyles(selected, show_results, is_winner);

    const outer = selected ? `` : ``;
    const square = selected ? `border-selection bg-selection` : `border-gray-200 group-hover:border-gray-400`;

    return <div onClick={() => {
        onSelect();
    }} className={`rounded-xl relative group p-1.5 ${container} ${can_select ? "cursor-pointer" : ""} ${outer}`}
    >
        {show_results && <SelectedBg eliminated={!show_as_eliminated} votes_count={votes_count} selection={selection}
                                     voting_method={poll.voting_method} is_winner={is_winner} id={id} totals={totals}/>}
        <div className="grid z-10 relative gap-2.5" style={{gridTemplateColumns: '1.5rem 1fr auto'}}>
            {!show_results && !voted && <div className="w-6 flex items-center justify-center">
                <Square className={`h-5 w-5 flex items-center justify-center rounded-md border-2 ${square}`}>
                    {selected && <ChoiceIcon number={number} voting_method={poll.voting_method}/>}
                </Square>
            </div>}
            {show_your_results && <div className="w-6 flex items-center justify-center">
                <Square
                    className={`h-5 w-5 flex items-center justify-center rounded-md border-2 border-gray-400 bg-gray-400`}>
                    {selected && <ChoiceIcon number={number} voting_method={poll.voting_method}/>}
                </Square>
            </div>}
            {show_results && <div className="w-5 h-5 flex items-center justify-center">
                {selected &&
                    <ChoiceIcon is_winner={is_winner} show_results number={number} voting_method={poll.voting_method}/>}
            </div>}
            <div className={`text-sm font-medium`}>
                <span className={` ${label}`}>{text}{!text &&
                    <span className="text-gray-400 italic">Add Choice</span>}</span>
            </div>
            <div className="flex items-center pr-1">
                {show_results && <SimpleTooltip text={getVotesText(totals, id, selection, poll)}>
                    <div className={`text-xs ${is_winner ? "text-gray-200" : "text-gray-600"}`}>
                        {getPercent(votes_count / totals.total)}
                    </div>
                </SimpleTooltip>}
            </div>
        </div>
    </div>
}

function getChoiceMeta(poll, id, vote, totals, status, has_voted, show_results) {
    /*
        if(poll.type==='ranked-choice-all') {

        } else if(poll.type==='ranked-choice-any') {

        }

     */

    const selected = vote && vote[id];

    return {
        is_winner: voting_getIsWinner(poll, id, vote, totals, status),
        number: vote[id],
        show_your_results: !show_results && has_voted && status === 'open',
        can_select: status === 'open' && !has_voted,
        selected
    }
}

function randomIntFromInterval(min, max) { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

function prepDataForResolver(poll) {
    return {
        choices: poll.choices,
        choices_data: poll.choices_data,
        votes: poll.votes,
        voted: poll.voted
    }
}

function buildTotals(ch, poll, show_results) {
    let a = {};

    if (poll.voting_method === 'ranked-choice-all') {
        if (!show_results) {
            return {
                total: poll.voter_count
            }
        }
        const results = voting_rankedChoiceResolver(prepDataForResolver(poll));

        return {
            ...results,
            total: poll.voter_count
        }
    }

    if (poll.voting_method === 'event-datetime') {
        // what is the spec for totals for event-datetime?
        /*
        // 0 = no, 1 = yes, 2 = maybe, -1 = no response
        choices: { [choice_id]: { 0: 0, 1: 0, 2: 0, -1: 0 }
        ranked: [choice_id] // top 1 or 2
         */
        const a = voting_eventDateTimeResolver(prepDataForResolver(poll), true);

        return {
            ...a,
            total: poll.voter_count
        }
    }

    ch.forEach(id => {
        a[id] = poll.votes[id] ? Object.keys(poll.votes[id]).length : 0;
    });

    const total = Object.values(a).reduce((partialSum, a) => partialSum + a, 0);

    if (poll.voter_count) {
        a.total = poll.voter_count;
    } else {
        a.total = total;
    }

    return a;
}

function shouldShowResults(data, voted, status) {
    if (status === 'closed') {
        return true;
    } else if (data.show_results === 'immediately' && voted) {
        return true;
    } else {
        return false;
    }
}

function getPollStatus(poll) {

    if (poll.status) {
        if (poll.status === 'closed') {
            return 'closed';
        }
    }

    let m2 = dayjs(poll.closes);
    let m = dayjs();

    if (m2.isAfter(m)) {
        return 'open'
    }

    return 'closed'
}

function processLiveData(livedata) {
    return {
        ...livedata
    }
}

function buildPoll(preview, livedata) {
    if (livedata) {
        return processLiveData(livedata);
    }
    if (preview) {
        return {
            ...poll,
            title: preview.title,
            voting_method: preview.voting_method,
            choices: preview.choices,
            closes: preview.closes,
            choices_data: preview.choices_data,
        }
    }
    return {...poll}
}

function RankedChoiceRoundsTabs({selected, totals, poll, setSelection}) {
    const tabs = totals.rounds_display.map(rd => {
        return rd[0]
    });

    return <EntityTabs tabs={tabs.map(t => {
        return {
            id: t,
            label: t
        }
    })} onChangeTab={s => setSelection(s)} active={selected}/>;
}

export const rating_reactions = [
    {
        id: "bad",
        emoji: "😕",
        label: "Bad"
    },
    {
        id: "ok",
        emoji: "🙂",
        label: "Ok"
    },
    {
        id: "awesome",
        emoji: "😃",
        label: "Awesome"
    }
];

function buildRatingNumberArray() {
    let a = [];
    for (let i = 0; i < 11; i++) {
        a.push(i);
    }
    return a;
}

function buildRatingReactionsArray() {
    return [...rating_reactions];
}

function Reaction({meta, reaction, selectChoice}) {
    const bg = meta.selected ? `bg-selection text-white` : `bg-gray-100 text-gray-600 group-hover:bg-selection group-hover:text-white`;
    const text = meta.selected ? `bg-selection text-white` : `bg-gray-100 text-gray-600 transition-opacity opacity-0 group-hover:opacity-100 group-hover:bg-gray-100 group-hover:text-gray-600`;
    return <div onClick={() => selectChoice(reaction.id)} className="group p-1 cursor-pointer">
        <div className="flex justify-center">
            <div className={`${bg} h-10 w-10 flex justify-center items-center rounded-full text-center`}>
            <span className="text-3xl">
            {reaction.emoji}
            </span>
            </div>
        </div>
        <div className="pt-1 h-6 flex justify-center">
            <div className={`${text} text-xs text-center p-0.5 rounded-md inline-block px-1.5 font-medium`}>
                {reaction.label}
            </div>
        </div>
    </div>
}

function CommentBox({comment, setComment}) {

    return <div className="pt-2 pb-1">
        <FieldLabel label="Add a Comment"/>
        <FieldInput type="textarea" value={comment} onChange={(a, v) => setComment(v)}
                    placeholder="Do you have any feedback?"/>
        <div>All comments are anonymous.</div>
    </div>
}

function renderPollChoices(poll, vote, totals, status, voted, show_results, selection, selectChoice) {

    if (poll.voting_method === 'rating-number') {
        const numbers = buildRatingNumberArray();
        return <div className="grid gap-1 pt-2 pb-1" style={{gridTemplateColumns: `repeat(auto-fill, 2rem)`}}>
            {numbers.map(num => {
                const meta = getChoiceMeta(poll, num, vote, totals, status, voted, show_results);
                const bg = meta.selected ? `bg-selection text-white` : `bg-gray-100 text-gray-600 hover:bg-selection hover:text-white`;
                return <div onClick={() => {
                    if (meta.can_select) {
                        selectChoice(num)
                    }
                }}
                            className={`cursor-pointer flex ${bg} justify-center items-center h-8 transition-colors rounded-md`}
                            key={num}>
                    <div className="text-sm font-medium">
                        {num}
                    </div>
                </div>
            })}
        </div>
    }

    if (poll.voting_method === 'rating-reaction') {
        const reactions = buildRatingReactionsArray();
        return <div className="pt-2 pb-1 grid gap-2"
                    style={{gridTemplateColumns: `repeat(auto-fill, minmax(3rem,5rem))`}}>
            {reactions.map(reaction => {
                const meta = getChoiceMeta(poll, reaction.id, vote, totals, status, voted, show_results);
                return <Reaction selectChoice={(reaction_id) => {
                    if (meta.can_select) {
                        selectChoice(reaction_id)
                    }
                }} key={reaction.id} meta={meta} reaction={reaction}/>
            })}
        </div>
    }

    if (poll.voting_method === 'event-datetime') {
        return <div className="pt-2 pb-1 grid gap-2">
            <EventDatetimeView poll={poll} vote={vote} totals={totals} status={status} voted={voted}
                               show_results={show_results} selection={selection} selectChoice={(a, b) => {

                                   // why does this now have a can select for meta?
                    selectChoice(a, b)

            }}/>
        </div>
    }

    if (show_results && totals.total === 0) {
        return <div className="pt-2 pb-1">
            <div className="text-center text-gray-500">No votes cast.</div>
        </div>
    }

  //  console.log("RENDER POLL CHOICES 2", poll.voting_method, poll, voted, status, totals, show_results, selection)

    return <div className="space-y-0.5 mt-1">
        {poll.choices.map(id => {
            const meta = getChoiceMeta(poll, id, vote, totals, status, voted, show_results);

            return <Choice show_results={show_results} id={id} voted={voted} totals={totals} poll={poll}
                           key={id} selection={selection}
                           onSelect={() => {
                               if (meta.can_select) {
                                   selectChoice(id)
                               }
                           }} {...meta} {...poll.choices_data[id]} />
        })}
    </div>
}

function VotingMethodTag({voting_method}) {
    let text = "";

    if (voting_method === "rating-number") {
        text = "Rating";
    } else if (voting_method === "rating-reaction") {
        text = "Reaction";
    } else if (voting_method === "ranked-choice-all") {
        text = "Ranked Choice";
    } else if (voting_method === "single-choice") {
        text = "Single Choice";
    } else if (voting_method === "ranked-choice-any") {
        text = "Ranked Choice";
    } else if (voting_method === "event-datetime") {
        text = "Event Date/Time";
    }

    return <span className="text-gray-500 text-xs font-medium">
        {text}
    </span>
}

export function PollView({
                             preview, id, debug, handleUpdate = () => {
    }, livedata, loading
                         }) {
    const community = useCommunity();
    const [vote, setVote] = useState({});
    const [selection, setSelection] = useState("Results");
    const [comment, setComment] = useState("");
    const [poll, setPoll] = useState(buildPoll(preview, livedata));
    const [voted, hasVoted] = useState(false);

    const can_manage_poll = community.member_id === poll?.created_by || community.is_admin;

    const can_close_poll = !preview && can_manage_poll && poll?.status !== "closed";
    const can_delete_poll = !preview && can_manage_poll && poll?.status !== "deleted";

    useEffect(function () {
        setPoll(buildPoll(preview, livedata))
        if (livedata) {
            const has_voted = !!livedata.voted[community.member_id];
            hasVoted(has_voted);
            if (has_voted) {
                setVote(livedata.vote_history[community.member_id]);
            }
        }
    }, [livedata, preview])

    useEffect(function () {
        if (poll && getPollStatus(poll) === 'closed' && poll.status !== 'closed') {
            setPoll({...poll, status: 'closed'});
            // todo: update poll status via posts
        }
    }, [poll])

    function handleVote() {
        if (preview) {
            return;
        }

        // update poll

        api_votePoll(community, id, vote)
            .then(() => {

            })
        hasVoted(true);
        let np = {...poll};
        np.voted[community.member_id] = true;
        np.voter_count++;
        np.vote_history[community.member_id] = vote;
        const ventries = Object.entries(vote);
        ventries.forEach(([a, b]) => {
            if (np[`votes.${a}`]) {
                np[`votes.${a}`][`${community.member_id}`] = b;
            } else {
                np[`votes.${a}`] = {};
                np[`votes.${a}`][`${community.member_id}`] = b;
            }

        });
        setPoll(np);
    }

    function selectChoice(id, metadata) {
        let nv = voting_handleSelection(id, vote, poll, metadata);

        setVote({
            ...nv
        })
    }

    const status = getPollStatus(poll);

    const show_results = shouldShowResults(poll, voted, status);

    const disable_button = voting_shouldDisableButton(poll, vote);

    const totals = buildTotals(poll.choices, poll, show_results);

    let content;

    if (loading) {
        content = <div className="h-96">
            <InlineLoader padding={"p-10"}/>
        </div>
    } else if (poll.status === 'deleted') {
        content = <div>
            <div className="text-center text-gray-500 text-sm font-medium">
                This poll has been deleted
            </div>
        </div>
    } else {

        let items = [];

        if (can_close_poll) {
            items.push({
                label: "Close Poll",
                onClick: () => {
                    api_closePoll(community, id)
                        .then(() => {
                            handleUpdate();
                        })
                }
            })
        }

        if (can_delete_poll) {
            items.push({
                label: "Delete Poll",
                onClick: () => {
                    api_deletePoll(community, id)
                        .then(() => {
                            handleUpdate();
                        })
                }
            })
        }

        content = <div className="relative">
            <VotingMethodTag voting_method={poll.voting_method}/>
            <Title {...poll} />

            {renderPollChoices(poll, vote, totals, status, voted, show_results, selection, selectChoice)}

            {show_results && poll.voting_method === 'ranked-choice-all' && <div className="pt-2">
                <RankedChoiceRoundsTabs totals={totals} setSelection={(s) => setSelection(s)} poll={poll}
                                        selected={selection}/>
            </div>}
            {!voted && preview && preview.can_comment && <CommentBox comment={comment} setComment={setComment}/>}
            <div className="h-10 mt-1 flex items-center">
                <Actions voted={voted} status={status} show_results={show_results} totals={totals}
                         disable_button={disable_button}
                         vote={handleVote} poll={poll}/>
            </div>
            {!show_results && voted &&
                <div><span className="text-gray-600 text-xs">Results shown at poll close</span></div>}
            {show_results && poll.voting_method === 'ranked-choice-all' && totals.winner === 'none' && <div>
                <span className="text-gray-600 text-xs">Poll resulted in an even draw</span>
            </div>}
            {poll.status === 'draft' && <div className="text-gray-600 text-xs">
                Poll currently in draft status
            </div>}
            {items.length > 0 && <div className="absolute top-3 right-3">
                <DotsOptionsPopup items={items}/>
            </div>}

            {debug && <div>
            <pre className="text-xs p-2 bg-gray-100 border-gray-300 rounded-md overflow-y-scroll text-left"
                 style={{maxHeight: '200px'}}>{JSON.stringify({
                vote, totals, show_results, id, disable_button, status, poll
            }, undefined, 2)}</pre>

            </div>}
        </div>
    }

    return <div className="rounded-xl shadow-sm border border-gray-200 px-4 py-2 bg-white">
        {content}
    </div>
}